/**
 * @file PPD Purchase Helper Methods.
 */

import get from 'lodash.get';

import { PPD_CART_ITEM_TYPES } from '../constants/ppd-purchase';
import { DIGITAL_DOWNLOADS_MODE } from '../constants/print-packages';

/**
 * Given the cart and the cartItem props we're looking for,
 * returns if the given props represent an Item already selected.
 *
 * @param {Array<CartItem>} cart Current PPD shopping cart.
 * @param {String} itemId The ID to look for as a cart Item.
 * @param {PPD_CART_ITEM_TYPES} type The type of the cart Item to look for.
 * @param {PrintSize|PrintPackage} [sizeOrPackage] Print Bundle or Print Size of the cart item.
 */
const isCartItemSelected = (cart, itemId, type, sizeOrPackage) => {
  switch (type) {
    case PPD_CART_ITEM_TYPES.DOWNLOAD:
      return !!cart.find(
        cartItem => cartItem.type === type && cartItem.photo.id === itemId,
      );
    case PPD_CART_ITEM_TYPES.INDIVIDUAL_PRINT:
      return !!cart.find(
        cartItem =>
          cartItem.type === type &&
          cartItem.photo.id === itemId &&
          cartItem.printSize.id === sizeOrPackage.id,
      );
    case PPD_CART_ITEM_TYPES.PRINT_PACKAGE:
      return !!cart.find(
        cartItem =>
          cartItem.type === type &&
          cartItem.photo.id === itemId &&
          cartItem.printPackage.id === sizeOrPackage.id,
      );
    case PPD_CART_ITEM_TYPES.SUBSCRIPTION:
      return !!cart.find(
        cartItem =>
          cartItem.type === type && cartItem.selectedIdentity.id === itemId,
      );
    case PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT:
      return !!cart.find(
        cartItem =>
          cartItem.type === type &&
          `${cartItem.selectedIdentity.id}-${cartItem.albumCustomPrint.id}` ===
            itemId,
      );
    default:
      return true;
  }
};

/**
 * Based on the album settings, detects if
 * a user can purchase downloads & individual prints.
 *
 * @param {Array<CartItem>} cart Users current cart.
 * @param {Album} album The album the user is buying from.
 * @param {Object} selectedIdentity Current active selected identity.
 * @param {Account} user User account, containing his purchased print packages.
 * @returns {Boolean}
 */
const canPurchaseDownloadsAndPrints = (cart, album, selectedIdentity, user) => {
  // TODO: Properly handle identity_level_purchase once we get it into
  // photo_pricing or album level. From the PhotoPackage level this should not be done.
  const requirePackagePurchase = get(
    album,
    'photo_pricing.requires_package_purchase',
    false,
  );
  if (!requirePackagePurchase) {
    return true;
  }

  const userHasPurchasedPackages = !!get(
    user,
    'purchased_print_packages.records',
    [],
  ).find(
    purchasedPrintPackage =>
      get(purchasedPrintPackage, 'identity.id') ===
      get(selectedIdentity, 'identity.id'),
  );

  if (userHasPurchasedPackages) {
    return true;
  }

  const hasPackageInCart = !!cart.find(
    cartItem =>
      cartItem.type === PPD_CART_ITEM_TYPES.PRINT_PACKAGE &&
      get(cartItem, 'selectedIdentity.id') === get(selectedIdentity, 'id'),
  );

  return hasPackageInCart;
};

/**
 * Returns the price of a Hi-Res Download
 * based on the applied photo tags & the album photo pricing.
 *
 * @param {Photo} photo Photo item to get download price for.
 * @param {Album} album Album item including photo pricing.
 * @returns {Number} Price for the photo. 0 if not available.
 */
const getPpdDownloadPrice = (photo, album) => {
  const priceAlbumFolderTag = get(
    photo,
    'price_album_folder_tag.digital_downloads_price.value',
    0,
  );
  if (priceAlbumFolderTag > 0) {
    return priceAlbumFolderTag;
  }

  return get(album, 'photo_pricing.digital_downloads_price.value', 0);
};

/**
 * Given a photo for download and an album, determines if they
 * have different tags. This is done based on the pricing. When pricing
 * is the same, this information is irrelevant either way.
 *
 * @param {Photo} photo Photo item to get download price for.
 * @param {Album} album Album item including photo pricing.
 * @returns {boolean} Whether the price comes from the price tag or not.
 */
const isPpdDownloadPriceFromCustomTag = (photo, album) => {
  const photoPricingTag = get(photo, 'price_album_folder_tag');
  const photoTagPrice = get(
    photo,
    'price_album_folder_tag.digital_downloads_price.value',
    0,
  );
  const albumPhotoPricing = get(album, 'photo_pricing');
  const albumPhotoPrice = get(
    album,
    'photo_pricing.digital_downloads_price.value',
    0,
  );

  return (
    !!albumPhotoPricing &&
    !!photoPricingTag &&
    photoTagPrice !== albumPhotoPrice
  );
};

/**
 * Checks if a photo was previously purchased as an individual print.
 *
 * @param {Photo} photo The photo to check if print was purchased for.
 * @param {Account} user User account currently signed in.
 */
const isIndividualPrintPurchased = (photo, user) => {
  const userPrints = get(user, 'purchased_prints', []);
  return !!userPrints.find(purchasedPrintItem => {
    const purchasedPhotoItems = get(
      purchasedPrintItem,
      'purchased_prints_photos.records',
      [],
    );
    return !!purchasedPhotoItems.find(
      photoItem => photoItem.photo.id === photo.id,
    );
  });
};

/**
 * Checks if a photo was previously purchased as a print package.
 *
 * @param {Photo} photo The photo to check if print package was purchased for.
 * @param {Account} user User account currently signed in.
 * @returns {boolean} Whether the print package has been previously purchased.
 */
const isPrintPackagePurchased = (photo, user) => {
  const userPrintPackages = get(user, 'purchased_print_packages.records', []);

  return !!userPrintPackages.find(
    purchasedPackageItem => purchasedPackageItem.photo.id === photo.id,
  );
};

/**
 * Checks if the user has a download photo in cart.
 * This can be a download item, an included download or an upsold one.
 *
 * @param {Array<CartItem>} cart The PPD shopping cart.
 * @param {string} selectedIdentityId Optional selectedIdentityId to check for download in cart.
 * @returns {boolean} Whether the user has a download in cart or not.
 */
const hasDownloadInCart = (cart = [], selectedIdentityId) => {
  if (!cart) {
    return false;
  }

  return !!cart.find(cartItem => {
    const isDownloadItem = cartItem.type === PPD_CART_ITEM_TYPES.DOWNLOAD;

    const isDigitalDownloadAvailable =
      cartItem.type === PPD_CART_ITEM_TYPES.PRINT_PACKAGE &&
      get(cartItem, 'printPackage.print_package.digital_downloads_mode') !==
        DIGITAL_DOWNLOADS_MODE.NOT_INCLUDED;

    const printPackageIncludesDownload =
      cartItem.type === PPD_CART_ITEM_TYPES.PRINT_PACKAGE &&
      (get(cartItem, 'printPackage.print_package.digital_downloads_mode') ===
        DIGITAL_DOWNLOADS_MODE.INCLUDED ||
        get(cartItem, 'digitalDownloadUpsell', false));

    const isDownloadInCart =
      isDownloadItem ||
      (isDigitalDownloadAvailable && printPackageIncludesDownload);

    if (selectedIdentityId) {
      return (
        isDownloadInCart && cartItem.selectedIdentity.id === selectedIdentityId
      );
    }

    return isDownloadInCart;
  });
};

/**
 * Checks if the user has prints in cart.
 *
 * @param {Array<CartItem>} cart The PPD shopping cart.
 * @returns {boolean} Whether the user has a prints in cart or not.
 */
const hasPrintsInCart = (cart = []) => {
  if (!cart) {
    return false;
  }

  return !!cart.find(
    cartItem =>
      cartItem.type === PPD_CART_ITEM_TYPES.INDIVIDUAL_PRINT ||
      cartItem.type === PPD_CART_ITEM_TYPES.PRINT_PACKAGE,
  );
};

/**
 * Checks if the user has subscription in cart.
 *
 * @param {Array<CartItem>} cart The PPD shopping cart.
 * @param {String} selectedIdentityId Optional selected Identity ID. Will check if the identity has an item in cart.
 * @returns {boolean} Whether the user has a subscriptions in cart or not.
 */
const hasSubscriptionInCart = (cart = [], selectedIdentityId) => {
  if (!cart) {
    return false;
  }

  return !!cart.find(cartItem => {
    const isSubscriptionItem =
      cartItem.type === PPD_CART_ITEM_TYPES.SUBSCRIPTION;

    if (selectedIdentityId) {
      return (
        isSubscriptionItem &&
        cartItem.selectedIdentity.id === selectedIdentityId
      );
    }

    return isSubscriptionItem;
  });
};

/**
 * Determines whether the given cart has any album custom print items.
 *
 * @param {Array} cart - The cart.
 * @returns {boolean} - Whether the cart has album custom print items or not.
 */
const hasAlbumCustomPrintsInCart = (cart = []) => {
  if (!cart || !cart.length) {
    return false;
  }

  return cart.some(
    cartItem => cartItem.type === PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT,
  );
};

/**
 * Returns all the print packages that are available for a specific Photo.
 *
 * @param {Array<Object>} albumPrintPackages - All the print packages.
 * @param {Object} photo - The photo.
 * @returns {Array} - The available print packages for this photo.
 */
const getEligibleAlbumPrintPackagesForPhoto = (
  albumPrintPackages = [],
  photo,
) => {
  if (!albumPrintPackages.length || !photo) {
    return [];
  }

  const albumFolderPhotoTagId = get(photo, 'price_album_folder_tag.id');

  return albumPrintPackages.filter(albumPrintPackage => {
    const albumPrintPackageTags = get(
      albumPrintPackage,
      'album_folder_tags.records',
      [],
    );
    const albumPrintPackageTagIds = albumPrintPackageTags.map(({ id }) => id);
    const albumPrintPackageHasTags = albumPrintPackageTags.length > 0;
    const albumPrintPackageHasPhotoTag = albumPrintPackageTagIds.includes(
      albumFolderPhotoTagId,
    );

    return !albumPrintPackageHasTags || albumPrintPackageHasPhotoTag;
  });
};

/**
 * Sorts matched photos by whether they have a price tag or not (placing
 * the ones with tags last).
 *
 * @param {Array<Object>} photos - The matched photos.
 * @returns {Array} - The sorted array of photos.
 */
const sortMatchedPhotosByTag = photos => {
  return [...photos].sort((a, b) => {
    if (a.photo.price_album_folder_tag) {
      return 1;
    }

    if (b.photo.price_album_folder_tag) {
      return -1;
    }

    return 0;
  });
};

/**
 * Sorts photos by the size of the facebox,
 * displaying those with a larger facebox on top.
 *
 * @param {Array<Object>} photos Matched photos from the user containing comparedFaces.
 * @returns {Array<Object>} Sorted photos array.
 */
const sortMatchedPhotosByFacebox = photos => {
  const getSortedComparedFaces = (comparedFaces = []) =>
    [...comparedFaces].sort((a, b) => {
      // eslint-disable-next-line
      const [aX, aY, aWidth, aHeight] = a;
      // eslint-disable-next-line
      const [bX, bY, bWidth, bHeight] = b;

      const aSize = aWidth * aHeight;
      const bSize = bWidth * bHeight;

      if (aSize < bSize) {
        return 1;
      }

      if (aSize > bSize) {
        return -1;
      }

      return 0;
    });

  return [...photos].sort((a, b) => {
    const [faceboxA = []] = getSortedComparedFaces(a.comparedFaces);
    const [faceboxB = []] = getSortedComparedFaces(b.comparedFaces);

    // eslint-disable-next-line
    const [aX, aY, aWidth = 0, aHeight = 0] = faceboxA;
    // eslint-disable-next-line
    const [bX, bY, bWidth = 0, bHeight = 0] = faceboxB;

    const aSize = aWidth * aHeight;
    const bSize = bWidth * bHeight;

    if (aSize < bSize) {
      return 1;
    }

    if (aSize > bSize) {
      return -1;
    }

    return 0;
  });
};

/**
 * Given a PPD Cart, will return the appropriate items for the checkout mutation
 * or checkoutPrice query.
 *
 * @param {Array<Object>} cart Current PPD shopping cart.
 * @param {Object} album The album identities are purchasing/joining to.
 * @returns {Object} The items and custom print items.
 */
const getPpaCheckoutItems = (cart, album) => {
  if (!get(album, 'id')) {
    return {
      items: [],
      customPrintItems: [],
    };
  }

  const subscriptionItems = cart
    .filter(cartItem => cartItem.type === PPD_CART_ITEM_TYPES.SUBSCRIPTION)
    .map(({ selectedIdentity }) => ({
      identity_id: get(selectedIdentity, 'identity.id'),
      ...(get(album, 'subscription_album_folder.id', undefined)
        ? {
            album_folder_id: album.subscription_album_folder.id,
            join_albums: [album.id],
          }
        : {
            albums: [album.id],
          }),
    }));

  const customPrintItems = cart
    .filter(
      cartItem => cartItem.type === PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT,
    )
    .map(
      ({ selectedIdentity, albumCustomPrint, quantity, includeShipping }) => ({
        album_custom_print_id: albumCustomPrint.id,
        identity_id: get(selectedIdentity, 'identity.id'),
        quantity,
        include_shipping: includeShipping,
      }),
    );

  return {
    items: subscriptionItems,
    customPrintItems,
  };
};

/**
 * Returns the photo items (PPD options) for a given cart.
 *
 * @param {Array<Object>} cart - Current PPD shopping cart.
 * @param {Object} album - The album of the cart items.
 */
const getPhotoCheckoutItems = (cart, album) => {
  if (!get(album, 'id')) {
    return [];
  }

  const items = cart.map(cartItem => {
    switch (cartItem.type) {
      case PPD_CART_ITEM_TYPES.DOWNLOAD: {
        return {
          photo_id: cartItem.photo.id,
          album_id: album.id,
          digital_downloads_purchase: true,
          identity_id: get(cartItem, 'selectedIdentity.identity.id'),
        };
      }
      case PPD_CART_ITEM_TYPES.PRINT_PACKAGE: {
        return {
          photo_id: cartItem.photo.id,
          album_id: album.id,
          album_print_package_id: cartItem.printPackage.id,
          digital_downloads_purchase:
            (get(
              cartItem,
              'printPackage.print_package.digital_downloads_mode',
            ) === DIGITAL_DOWNLOADS_MODE.INCLUDED ||
              get(cartItem, 'digitalDownloadUpsell', false)) &&
            get(
              cartItem,
              'printPackage.print_package.digital_downloads_mode',
            ) !== DIGITAL_DOWNLOADS_MODE.NOT_INCLUDED,
          quantity: cartItem.quantity,
          identity_id: get(cartItem, 'selectedIdentity.identity.id'),
        };
      }
      case PPD_CART_ITEM_TYPES.INDIVIDUAL_PRINT: {
        return {
          photo_id: cartItem.photo.id,
          album_id: album.id,
          photo_pricing_print_item_id: cartItem.printSize.id,
          quantity: cartItem.quantity,
          identity_id: get(cartItem, 'selectedIdentity.identity.id'),
        };
      }

      default: {
        return null;
      }
    }
  });

  return items.filter(item => !!item);
};

/**
 * Returns cart items for use with `checkout` mutation and `checkoutPrice` query.
 *
 * @param {Array<Object>} cart - The cart.
 * @param {Object} album - The album.
 * @returns {Object} - The checkout items.
 */
const getAlbumCheckoutItems = (cart, album) => {
  if (!get(album, 'id')) {
    return {
      ppaItems: [],
      customPrintItems: [],
      photoItems: [],
    };
  }

  const { items, customPrintItems } = getPpaCheckoutItems(cart, album);

  return {
    ppaItems: items,
    customPrintItems,
    photoItems: getPhotoCheckoutItems(cart, album),
  };
};

/**
 * Given a Price item with shipping cost and/or taxes, will remove the latter 2
 * leaving only the price before shipping & taxes.
 *
 * @param {Object} price - The price item to remove addons from.
 * @returns {number} - The final price without shipping / taxes.
 */
const getPriceWithoutAddons = price =>
  get(price, 'total_price.value', 0) -
  get(price, 'total_shipping_cost.value', 0) -
  get(price, 'total_tax.value', 0);

/**
 * Returns the given selected identity's purchased custom prints.
 *
 * @param {Object} selectedIdentity - The selected identity.
 * @param {Array<Object>} purchasedCustomPrints - An array of purchased custom prints.
 * @returns {Array} - The selected identity's purchased custom prints.
 */
const getSelectedIdentityPurchasedCustomPrints = (
  selectedIdentity,
  purchasedCustomPrints,
) => {
  if (
    !selectedIdentity ||
    !purchasedCustomPrints ||
    !purchasedCustomPrints.length
  ) {
    return [];
  }

  return purchasedCustomPrints.filter(
    purchasedCustomPrint =>
      purchasedCustomPrint?.selected_identity?.id === selectedIdentity.id,
  );
};

/**
 * Determines whether the album purchase/ppd screens should
 * show the top header brand attribution banner.
 *
 * @param {Object} album - The album.
 * @returns {boolean}
 */
const shouldDisplayBrandAttribution = album => {
  if (!album) {
    return false;
  }

  const parentAlbumFolder =
    get(album, 'album_folder') || get(album, 'root_album_folder') || {};
  const displayPhotosAttribution =
    album.display_photos_attribution ||
    parentAlbumFolder.display_photos_attribution;

  return !!album.creator && displayPhotosAttribution;
};

export {
  isCartItemSelected,
  canPurchaseDownloadsAndPrints,
  getPpdDownloadPrice,
  isIndividualPrintPurchased,
  isPrintPackagePurchased,
  isPpdDownloadPriceFromCustomTag,
  hasDownloadInCart,
  hasPrintsInCart,
  hasAlbumCustomPrintsInCart,
  getEligibleAlbumPrintPackagesForPhoto,
  sortMatchedPhotosByTag,
  sortMatchedPhotosByFacebox,
  hasSubscriptionInCart,
  getPriceWithoutAddons,
  getSelectedIdentityPurchasedCustomPrints,
  getAlbumCheckoutItems,
  shouldDisplayBrandAttribution,
};
